import { has } from "lodash";
import { AbstractDate } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { Card, GeoCard } from "..";
import { GeoTerm, SearchFragmentGeo } from "@radarly/models/search";
import { isGeoCardDataviz } from "../dataviz";
import { ComparisonPeriod } from "../period";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface GeoCardFactoryOptions {
  term?: GeoTerm;
  from?: AbstractDate;
  metric?: Metric;
  to?: AbstractDate;
  clause?: Clause;
  comparisonPeriod?: ComparisonPeriod;
}

export class GeoCardFragmentsFactory extends CardFragmentsFactory {
  static create(input: GeoCardFactoryOptions) {
    const factory = new GeoCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input: GeoCardFactoryOptions) {
    const factory = new GeoCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      term: factory.getTermFromCard(card, input) as GeoTerm,
      metric: factory.getMetricFromCardAndInput(card, input),
      comparisonPeriod: factory.getComparaisonPeriod(
        card,
        "previous_period",
        input
      ),
    });
  }
  private getFragments(input?: GeoCardFactoryOptions) {
    return this.getComparaisonFragments(
      this.getFragment(input),
      input && has(input, "comparisonPeriod")
        ? input.comparisonPeriod
        : "previous_period"
    );
  }
  private getFragment(input?: GeoCardFactoryOptions): SearchFragmentGeo {
    const term = input?.term || Facet.Countries;
    const metric = input?.metric || Metric.Doc;
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          metrics: { metrics: [metric] },
          [term]: {
            metrics: [metric],
            size: 250,
            sort: {
              on: metric,
              order: "desc",
            },
          },
        },
      },
    };
  }
}
export class GeoCardFactory
  implements ICardFactory<GeoCard, GeoCardFactoryOptions>
{
  public static getInstance() {
    return new GeoCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: GeoCardFactoryOptions & {
      dataviz?: GeoCard["dataviz"];
    }
  ) {
    return CardFactory.getInstance<GeoCard>().createCard(
      workspaceId,
      projectFocuses,
      "geo",
      card.title,
      GeoCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: GeoCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<GeoCard>().createCard(
      workspaceId,
      projectFocuses,
      "geo",
      title,
      GeoCardFragmentsFactory.create(input),
      this.createDataviz(
        getDefaultDataviz(input?.dataviz || DatavizName.HorizontalBarChart)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isGeoCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.HorizontalBarChart
      ) as GeoCard["dataviz"];
    }
  }
}
